// Verdict de puissance de l'appareil, Android uniquement.
//
// Le timbre "Default" (cle interne 'lush') empile deux moteurs sur chaque
// accord : la nappe de multiNotePlayer et le piano echantillonne de
// chordInstruments, chacun avec sa propre reverb. Sur un appareil qui tient
// tout juste la cadence, cette superposition est ce qui fait deborder le rendu
// audio et crachotter le son.
//
// Plutot que de deviner d'apres le nombre de coeurs ou la RAM annoncee (un
// Galaxy A50 annonce 8 coeurs et 4 Go, il passerait pour costaud), on mesure ce
// que l'appareil met reellement a decoder les loops au demarrage. Le verdict est
// persiste : il s'applique donc des le lancement suivant, sans remesure.
//
// iOS et web : isLightDevice() rend toujours false, aucun chemin ne change.

import { isAndroid } from './platformUtils';

// localStorage et non le mirror utilisateur, pour deux raisons verifiees :
// - le mirror n'est attache a IndexedDB qu'apres un effet asynchrone de
//   DataProvider, souvent apres la fin du prechargement : le verdict n'etait
//   alors ecrit qu'en memoire et perdu au redemarrage, d'ou un mode allege qui
//   changeait a chaque lancement ;
// - le scope 'setting' est marque dirty et pousse vers le cloud par syncEngine,
//   donc le verdict d'un appareil aurait voyage vers les autres appareils du
//   compte. Or il decrit CETTE machine, pas l'utilisateur.
const STORAGE_KEY = 'upScale.deviceIsLight';

const readStored = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw === null ? null : raw === 'true';
  } catch (_) {
    return null;
  }
};

const writeStored = (value) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, value ? 'true' : 'false');
  } catch (_) {
    // Stockage indisponible : le verdict tient au moins pour cette session.
  }
};

// Millisecondes de decodage par loop FLAC, chronometrees pendant le
// prechargement prioritaire du PREMIER lancement, banque piano en concurrence.
// Deux appareils reels servent de bornes, mesures en build debug :
//
//   Galaxy A50 (Exynos 9610, Android 11)    383 ms/loop  -> a besoin du mode allege
//   Lenovo Tab M10 5G (Android 14)          184 ms/loop  -> tient la superposition
//
// La tablette a ete mesuree sans un seul decrochage en timbre complet, y compris
// en martelant les notes plus vite qu'un humain : la degrader n'apporterait rien
// et lui couterait le piano. Le seuil est pose entre les deux mesures.
//
// La valeur absolue varie avec le type de build et l'etat du cache disque ; c'est
// pourquoi la mesure n'a lieu qu'une fois, dans les conditions du premier
// lancement, et que le verdict est ensuite fige (voir recordPreloadTiming).
const LIGHT_DEVICE_MS_PER_LOOP = 280;

// Verdict mesure pendant CETTE session. null tant que le prechargement n'a pas
// rendu son chrono.
let measured = null;

/**
 * True quand l'appareil a ete juge trop juste pour la superposition nappe +
 * piano. Toujours false hors Android.
 *
 * Relit le stockage a chaque appel plutot que de figer une valeur au chargement
 * du module : le singleton audioEngine interroge cette fonction des sa
 * construction, et la mesure de la session peut tomber apres.
 */
export const isLightDevice = () => {
  if (!isAndroid()) return false;
  if (measured !== null) return measured;
  return readStored() === true;
};

/**
 * Enregistre le temps qu'a pris le prechargement prioritaire et en tire le
 * verdict. Appele une fois par lancement, a la fin du lot prioritaire.
 */
export const recordPreloadTiming = (totalMs, loopCount) => {
  if (!isAndroid() || !loopCount) return isLightDevice();

  // Un verdict deja rendu ne se remesure pas. La duree du prechargement depend
  // de ce qui se charge en parallele, or le verdict change justement cela : en
  // mode allege la banque piano n'est plus prechauffee, le lot prioritaire va
  // donc plus vite, et l'appareil repasse pour suffisant... jusqu'au lancement
  // suivant, ou le piano revient et le ralentit de nouveau.
  // Constate sur Galaxy A50 : "Pad" au premier lancement, "Default" au second,
  // en alternance. On fige donc le verdict sur la premiere mesure, faite dans
  // les conditions les plus chargees, donc les plus representatives.
  const stored = readStored();
  if (stored !== null) {
    measured = stored;
    return measured;
  }

  const perLoop = totalMs / loopCount;
  const light = perLoop > LIGHT_DEVICE_MS_PER_LOOP;
  measured = light;
  writeStored(light);
  console.log(
    `[DevicePerf] ${loopCount} loops en ${Math.round(totalMs)}ms → ${Math.round(perLoop)}ms/loop, seuil ${LIGHT_DEVICE_MS_PER_LOOP}ms → ${light ? 'appareil leger' : 'appareil suffisant'}`
  );
  return light;
};

export default { isLightDevice, recordPreloadTiming };
